import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { getCompanyId } from "@lhd/utility";
import { AppCategory } from "types/app.type";

export const useFetchAppCategories = (
  filterParams?: any,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();
  const { __BLOCK_API__, ...restFilterParams } = filterParams || {};
  return useQuery(
    [QUERY_NAME.APP_CATEGORIES, restFilterParams, page, pageSize],
    () =>
      axios({
        method: "get",
        url: SERVICES.APP_CATEGORIES,
        params: {
          ...restFilterParams,
          page,
          per_page: pageSize,
          current_company_id: companyId,
        },
      }).then((res) => ({
        data: res.data as AppCategory[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !__BLOCK_API__,
    },
  );
};

export const useFetchOneAppCategory = (appCategoryId: any) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.APP_CATEGORY, appCategoryId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APP_CATEGORIES}/${appCategoryId}`,
        params: {
          current_company_id: companyId,
        },
      }).then((res) => res.data as AppCategory),
    {
      enabled: !!appCategoryId,
      cacheTime: 60000,
      staleTime: 60000,
    },
  );
};

export const useDeleteAppCategory = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ appCategoryId }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.APP_CATEGORIES}/${appCategoryId}`,
        params: {
          current_company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APP_CATEGORIES]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteMultipleAppCategorys = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ listIds }: any) => {
      const formData = new FormData();
      listIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      formData.append("current_company_id", companyId.toString());
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.APP_CATEGORIES}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APP_CATEGORIES]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useSubmitAppCategory = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, appCategoryId }: any) => {
      const formData = new FormData();
      Object.keys(payload).forEach((key) => {
        if (payload[key] !== undefined && payload[key] !== null) {
          formData.append(key, payload[key]);
        }
      });
      formData.append("current_company_id", companyId.toString());
      if (payload.company_id) {
        formData.append("company_id", payload.company_id.toString());
      }

      return axios({
        method: appCategoryId ? "patch" : "post",
        url: appCategoryId
          ? `${SERVICES.APP_CATEGORIES}/${appCategoryId}`
          : SERVICES.APP_CATEGORIES,
        data: formData,
        params: {
          current_company_id: companyId,
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APP_CATEGORIES]);
        queryClient.invalidateQueries([QUERY_NAME.APP_CATEGORY]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useSaveAppCategories = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APP_CATEGORIES_UPDATE_SORT_INDEX}`,
        data: {
          category_data: payload,
          company_id: companyId,
          current_company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APP_CATEGORIES]);
        queryClient.invalidateQueries([QUERY_NAME.APP_CATEGORY]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        notification.error({
          message:
            error?.response?.data?.msg ||
            error?.response?.data?.error ||
            "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchAppCategoryFilterOptions = (
  payload: any,
  enabled: boolean,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.APP_CATEGORIES, "filter_options", payload],
    () =>
      axios({
        method: "post",
        url: `${SERVICES.APP_CATEGORIES}/filter_options`,
        data: {
          ...payload,
          current_company_id: companyId,
        },
      }).then((res) => res.data),
    {
      enabled,
      cacheTime: 5 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
    },
  );
};
